
import React, { useState } from "react";
import { 
  Table, 
  TableBody, 
  TableCaption, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { Calendar, Clock, Download, Filter, Plus } from "lucide-react";

// Sample data for attendance
const sampleAttendance = [
  {
    id: "1",
    employeeId: "1",
    employeeName: "Ahmad Bin Ali",
    date: "2023-05-18",
    clockIn: "08:03:22",
    clockOut: "17:05:45",
    hoursWorked: 9.03,
    status: "Present",
    isLate: true,
    isEarlyOut: false,
    remarks: "Traffic jam"
  },
  {
    id: "2",
    employeeId: "2",
    employeeName: "Siti Aminah",
    date: "2023-05-18",
    clockIn: "07:55:10",
    clockOut: "17:02:30",
    hoursWorked: 9.12,
    status: "Present",
    isLate: false,
    isEarlyOut: false,
    remarks: ""
  },
  {
    id: "3",
    employeeId: "3",
    employeeName: "Rajesh Kumar",
    date: "2023-05-18",
    clockIn: "",
    clockOut: "",
    hoursWorked: 0,
    status: "Absent",
    isLate: false,
    isEarlyOut: false,
    remarks: "Sick leave"
  },
  {
    id: "4",
    employeeId: "1",
    employeeName: "Ahmad Bin Ali",
    date: "2023-05-17",
    clockIn: "08:01:05",
    clockOut: "17:00:30",
    hoursWorked: 8.99,
    status: "Present",
    isLate: true,
    isEarlyOut: false,
    remarks: ""
  },
  {
    id: "5",
    employeeId: "2",
    employeeName: "Siti Aminah",
    date: "2023-05-17",
    clockIn: "07:58:42",
    clockOut: "16:45:15",
    hoursWorked: 8.78,
    status: "Present",
    isLate: false,
    isEarlyOut: true,
    remarks: "Personal errands"
  }
];

// Sample employee list
const employees = [
  { id: "1", name: "Ahmad Bin Ali" },
  { id: "2", name: "Siti Aminah" },
  { id: "3", name: "Rajesh Kumar" }
];

interface AttendanceFormData {
  employeeId: string;
  date: string;
  clockIn: string;
  clockOut: string;
  status: string;
  remarks: string;
}

const Attendance = () => {
  const [attendances, setAttendances] = useState(sampleAttendance);
  const [isAddAttendanceOpen, setIsAddAttendanceOpen] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  
  const form = useForm<AttendanceFormData>({
    defaultValues: {
      employeeId: "",
      date: new Date().toISOString().split('T')[0],
      clockIn: "",
      clockOut: "",
      status: "Present",
      remarks: ""
    }
  });

  const handleAddAttendance = (data: AttendanceFormData) => {
    const employee = employees.find(emp => emp.id === data.employeeId);
    if (!employee) return;
    
    const clockInTime = data.clockIn ? data.clockIn : "";
    const clockOutTime = data.clockOut ? data.clockOut : "";
    
    // Calculate hours worked if both times are provided
    let hoursWorked = 0;
    if (clockInTime && clockOutTime) {
      const clockIn = new Date(`${data.date}T${clockInTime}`);
      const clockOut = new Date(`${data.date}T${clockOutTime}`);
      hoursWorked = (clockOut.getTime() - clockIn.getTime()) / (1000 * 60 * 60);
    }
    
    // Check if late (after 8:00 AM)
    const isLate = clockInTime ? new Date(`${data.date}T${clockInTime}`).getTime() > 
                                 new Date(`${data.date}T08:00:00`).getTime() : false;
    
    // Check if early out (before 5:00 PM)
    const isEarlyOut = clockOutTime ? new Date(`${data.date}T${clockOutTime}`).getTime() < 
                                     new Date(`${data.date}T17:00:00`).getTime() : false;
    
    const newAttendance = {
      id: `${attendances.length + 1}`,
      employeeId: data.employeeId,
      employeeName: employee.name,
      date: data.date,
      clockIn: clockInTime,
      clockOut: clockOutTime,
      hoursWorked: parseFloat(hoursWorked.toFixed(2)),
      status: data.status,
      isLate,
      isEarlyOut,
      remarks: data.remarks
    };
    
    setAttendances([...attendances, newAttendance]);
    setIsAddAttendanceOpen(false);
    form.reset();
  };

  const handleExportReport = () => {
    // In a real app, this would generate and download a report
    alert("Exporting attendance report... This feature would be implemented with a proper export library");
  };

  // Filter attendances based on filters
  const filteredAttendances = attendances.filter(attendance => {
    const matchesEmployee = filterEmployee ? attendance.employeeId === filterEmployee : true;
    const matchesDate = filterDate ? attendance.date === filterDate : true;
    const matchesStatus = filterStatus ? attendance.status === filterStatus : true;
    
    return matchesEmployee && matchesDate && matchesStatus;
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-2xl font-semibold">Attendance Tracking</h2>
        <div className="flex flex-wrap gap-2">
          <Dialog open={isAddAttendanceOpen} onOpenChange={setIsAddAttendanceOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Manual Entry
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Add Attendance Record</DialogTitle>
              </DialogHeader>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleAddAttendance)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="employeeId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Employee</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select employee" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {employees.map(employee => (
                              <SelectItem key={employee.id} value={employee.id}>
                                {employee.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Date</FormLabel>
                        <FormControl>
                          <div className="flex">
                            <Calendar className="mr-2 h-4 w-4 mt-3" />
                            <Input type="date" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="clockIn"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Clock-in Time</FormLabel>
                          <FormControl>
                            <div className="flex">
                              <Clock className="mr-2 h-4 w-4 mt-3" />
                              <Input type="time" {...field} />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="clockOut"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Clock-out Time</FormLabel>
                          <FormControl>
                            <div className="flex">
                              <Clock className="mr-2 h-4 w-4 mt-3" />
                              <Input type="time" {...field} />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Present">Present</SelectItem>
                            <SelectItem value="Absent">Absent</SelectItem>
                            <SelectItem value="Leave">Leave</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="remarks"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Remarks</FormLabel>
                        <FormControl>
                          <Input placeholder="Any comments or notes..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="flex justify-end">
                    <Button type="submit">Save Record</Button>
                  </div>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
          
          <Button variant="outline" onClick={handleExportReport}>
            <Download className="mr-2 h-4 w-4" />
            Export Report
          </Button>
        </div>
      </div>
      
      <div className="flex flex-col md:flex-row gap-4">
        <Select
          value={filterEmployee}
          onValueChange={setFilterEmployee}
        >
          <SelectTrigger className="w-full md:w-[200px]">
            <Filter className="mr-2 h-4 w-4" />
            <span>{filterEmployee ? employees.find(e => e.id === filterEmployee)?.name : "Filter by Employee"}</span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">All Employees</SelectItem>
            {employees.map(employee => (
              <SelectItem key={employee.id} value={employee.id}>
                {employee.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <div className="flex items-center w-full md:w-auto">
          <Calendar className="mr-2 h-4 w-4" />
          <Input 
            type="date" 
            value={filterDate} 
            onChange={(e) => setFilterDate(e.target.value)}
            className="w-full"
          />
        </div>
        
        <Select
          value={filterStatus}
          onValueChange={setFilterStatus}
        >
          <SelectTrigger className="w-full md:w-[200px]">
            <Filter className="mr-2 h-4 w-4" />
            <span>{filterStatus || "Filter by Status"}</span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">All Statuses</SelectItem>
            <SelectItem value="Present">Present</SelectItem>
            <SelectItem value="Absent">Absent</SelectItem>
            <SelectItem value="Leave">Leave</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="rounded-md border">
        <Table>
          <TableCaption>Attendance records for employees</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Clock-in</TableHead>
              <TableHead>Clock-out</TableHead>
              <TableHead>Hours</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Remarks</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAttendances.length > 0 ? (
              filteredAttendances.map((attendance) => (
                <TableRow key={attendance.id}>
                  <TableCell className="font-medium">{attendance.employeeName}</TableCell>
                  <TableCell>{attendance.date}</TableCell>
                  <TableCell>
                    <span className={attendance.isLate ? "text-red-500" : ""}>
                      {attendance.clockIn || "N/A"}
                    </span>
                    {attendance.isLate && (
                      <span className="block text-xs text-red-500">Late</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={attendance.isEarlyOut ? "text-amber-500" : ""}>
                      {attendance.clockOut || "N/A"}
                    </span>
                    {attendance.isEarlyOut && (
                      <span className="block text-xs text-amber-500">Early out</span>
                    )}
                  </TableCell>
                  <TableCell>{attendance.hoursWorked > 0 ? attendance.hoursWorked : "N/A"}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      attendance.status === "Present" 
                        ? "bg-green-100 text-green-800" 
                        : attendance.status === "Absent"
                          ? "bg-red-100 text-red-800"
                          : "bg-blue-100 text-blue-800"
                    }`}>
                      {attendance.status}
                    </span>
                  </TableCell>
                  <TableCell>{attendance.remarks || "No remarks"}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-4">
                  No attendance records found matching the filter criteria
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default Attendance;
